import { useMemo, useState } from 'react'
import { api } from '../api/client'
import { useResource, useToast } from '../api/hooks'
import type { Account, CreateAccountResult, DoorListItem, Group } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageHeader } from '../components/Layout'
import {
  Alert, Card, ConfirmDialog, ErrorBox, Field, GroupChips, Spinner, Toast, describeError, relative,
} from '../components/Ui'
import { de, fmt } from '../i18n/de'

export function AccountsPage() {
  const { me } = useAuth()
  const accounts = useResource<Account[]>('/accounts')
  const groups = useResource<Group[]>('/groups')
  const doors = useResource<DoorListItem[]>('/doors')
  const { toast, show, clear } = useToast()

  const [showCreate, setShowCreate] = useState(false)
  const [oneTime, setOneTime] = useState<CreateAccountResult | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Account | null>(null)

  if (accounts.loading) return <Spinner />
  if (accounts.error) return <ErrorBox error={accounts.error} onRetry={accounts.reload} />

  const act = async (action: () => Promise<unknown>) => {
    try {
      await action()
      accounts.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const resetPassword = async (account: Account) => {
    try {
      setOneTime(await api.post<CreateAccountResult>(`/accounts/${account.id}/password/reset`))
      accounts.reload()
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const remove = async (account: Account) => {
    try {
      await api.delete(`/accounts/${account.id}`)
      setConfirmDelete(null)
      accounts.reload()
    } catch (error) {
      setConfirmDelete(null)
      show(describeError(error), 'warn')
    }
  }

  return (
    <>
      <PageHeader title={de.accounts.title} subtitle={de.accounts.intro}
                  actions={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    {de.accounts.create}
                  </button>} />

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>{de.accounts.columnName}</th>
              <th>{de.accounts.columnEmail}</th>
              <th>{de.accounts.columnRole}</th>
              <th>{de.accounts.columnVisibility}</th>
              <th>{de.accounts.columnLastLogin}</th>
              <th style={{ width: 260 }} />
            </tr>
          </thead>
          <tbody>
            {(accounts.data ?? []).map(account => (
              <tr key={account.id}>
                <td>
                  <strong>{account.displayName}</strong>
                  {!account.isActive && <span className="faint xs"> · {de.app.inactive}</span>}
                  {account.isLocked && (
                    <span className="status status-warn xs"> · {de.accounts.locked}</span>
                  )}
                </td>
                <td className="muted">{account.email}</td>
                <td>{account.role === 'admin' ? de.accounts.roleAdmin
                   : account.role === 'user' ? de.accounts.roleUser : 'Superadmin'}</td>
                <td>
                  {account.groupScope === 'open'
                    ? <span className="muted">{de.accounts.visibilityOpen}</span>
                    : <GroupChips groups={account.groups} />}
                </td>
                <td className={account.lastLoginAt ? 'mono muted' : undefined}>
                  {account.lastLoginAt
                    ? relative(account.lastLoginAt)
                    : <span className="status status-warn" title={de.accounts.neverHint}>
                        {de.accounts.never} ⚠
                      </span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-ghost" onClick={() => void resetPassword(account)}>
                    {de.accounts.resetPassword}
                  </button>
                  {account.id !== me?.accountID && (
                    <>
                      <button className="btn btn-ghost"
                              onClick={() => void act(() =>
                                api.patch(`/accounts/${account.id}`, { isActive: !account.isActive }))}>
                        {account.isActive ? de.doorcodes.deactivate : de.doorcodes.activate}
                      </button>
                      <button className="btn btn-ghost" style={{ color: 'var(--danger)' }}
                              onClick={() => setConfirmDelete(account)}>
                        {de.app.delete}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            {(accounts.data ?? []).length === 0 && (
              <tr><td colSpan={6} className="faint sm">{de.app.noEntries}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <AccountDialog groups={groups.data ?? []} doors={doors.data ?? []}
                       onClose={() => setShowCreate(false)}
                       onCreated={result => { setShowCreate(false); setOneTime(result); accounts.reload() }}
                       onError={message => show(message, 'warn')} />
      )}

      {oneTime && (
        <OneTimePasswordDialog result={oneTime} onClose={() => setOneTime(null)} />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={fmt(de.accounts.confirmDelete, { name: confirmDelete.displayName })}
          detail={de.accounts.confirmDeleteDetail}
          confirmLabel={de.app.delete}
          onConfirm={() => void remove(confirmDelete)}
          onCancel={() => setConfirmDelete(null)} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

function AccountDialog({ groups, doors, onClose, onCreated, onError }: {
  groups: Group[]
  doors: DoorListItem[]
  onClose: () => void
  onCreated: (result: CreateAccountResult) => void
  onError: (message: string) => void
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'user'>('user')
  const [restricted, setRestricted] = useState(false)
  const [groupIDs, setGroupIDs] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  /**
   * Löst live auf, welche Türen aus der Gruppenauswahl folgen. Ohne diese Zeile müsste der
   * Admin die Gruppen-Tür-Zuordnung im Kopf haben.
   */
  const resolvedDoors = useMemo(() => {
    const ids = new Set<string>()
    for (const group of groups) {
      if (groupIDs.has(group.id)) group.doorIDs.forEach(id => ids.add(id))
    }
    return doors.filter(door => ids.has(door.id)).map(door => door.name)
  }, [groups, groupIDs, doors])

  const submit = async () => {
    setBusy(true)
    try {
      onCreated(await api.post<CreateAccountResult>('/accounts', {
        email, firstName, lastName, role,
        groupScope: role === 'user' && restricted ? 'restricted' : 'open',
        groupIDs: [...groupIDs],
      }))
    } catch (error) {
      onError(describeError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 60, padding: 'var(--sp-5)', overflow: 'auto',
    }} role="dialog" aria-modal="true">
      <div className="card" style={{ width: 520, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-h">{de.accounts.create}</div>
        <div className="card-b">
          <div className="grid cols-2" style={{ gap: 'var(--sp-3)' }}>
            <Field label={de.doorcodes.firstName}>
              <input type="text" value={firstName} onChange={event => setFirstName(event.target.value)} />
            </Field>
            <Field label={de.doorcodes.lastName}>
              <input type="text" value={lastName} onChange={event => setLastName(event.target.value)} />
            </Field>
          </div>

          <Field label={de.accounts.columnEmail} help={de.accounts.emailHelp}>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} />
          </Field>

          <Field label={de.accounts.columnRole}>
            <div className="inline-opts">
              <label>
                <input type="radio" checked={role === 'admin'} onChange={() => setRole('admin')} />
                <span><strong>{de.accounts.roleAdmin}</strong> — {de.accounts.roleAdminHelp}</span>
              </label>
              <label>
                <input type="radio" checked={role === 'user'} onChange={() => setRole('user')} />
                <span><strong>{de.accounts.roleUser}</strong> — {de.accounts.roleUserHelp}</span>
              </label>
            </div>
          </Field>

          {role === 'user' && (
            <Field label={de.accounts.visibility}>
              <div className="inline-opts">
                <label>
                  <input type="radio" checked={!restricted} onChange={() => setRestricted(false)} />
                  {de.accounts.visibilityOpen}
                </label>
                <label>
                  <input type="radio" checked={restricted} onChange={() => setRestricted(true)} />
                  {de.accounts.visibilityRestricted}
                </label>
              </div>

              {restricted && (
                <>
                  <div style={{ margin: 'var(--sp-2) 0 0 var(--sp-5)', display: 'grid',
                                gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-1)' }}>
                    {groups.map(group => (
                      <label key={group.id} className="sm" style={{ display: 'flex', gap: 6 }}>
                        <input type="checkbox" checked={groupIDs.has(group.id)}
                               onChange={event => setGroupIDs(current => {
                                 const next = new Set(current)
                                 if (event.target.checked) next.add(group.id)
                                 else next.delete(group.id)
                                 return next
                               })} />
                        {group.name}
                      </label>
                    ))}
                  </div>

                  <div style={{ marginTop: 'var(--sp-3)' }}>
                    <Alert kind="info">
                      <strong>{fmt(de.accounts.resolvedDoors, {
                        doors: resolvedDoors.length > 0 ? resolvedDoors.join(', ') : de.app.none,
                      })}</strong>
                    </Alert>
                  </div>
                </>
              )}
            </Field>
          )}

          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end',
                        marginTop: 'var(--sp-4)' }}>
            <button className="btn btn-ghost" onClick={onClose}>{de.app.cancel}</button>
            <button className="btn btn-primary"
                    disabled={busy || !email.includes('@')
                              || (role === 'user' && restricted && groupIDs.size === 0)}
                    onClick={() => void submit()}>
              {de.accounts.create}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Das Einmalpasswort wird einmal gezeigt und ist danach nicht mehr abrufbar. */
function OneTimePasswordDialog({ result, onClose }: {
  result: CreateAccountResult
  onClose: () => void
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 61,
    }} role="dialog" aria-modal="true">
      <div className="card" style={{ width: 420, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-h">{de.accounts.oneTimePassword}</div>
        <div className="card-b">
          <div className="sm muted" style={{ marginBottom: 'var(--sp-3)' }}>
            {result.account.displayName} · {result.account.email}
          </div>

          <div className="code-display" style={{ textAlign: 'center', letterSpacing: '.08em',
                                                 fontSize: 'var(--fs-xl)' }}>
            {result.oneTimePassword}
          </div>

          <div className="help" style={{ textAlign: 'center', marginTop: 'var(--sp-3)' }}>
            {de.accounts.oneTimePasswordHint}
          </div>

          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end',
                        marginTop: 'var(--sp-4)' }}>
            <button className="btn btn-ghost"
                    onClick={() => void navigator.clipboard?.writeText(result.oneTimePassword)}>
              ⧉ {de.app.copy}
            </button>
            <button className="btn btn-primary" onClick={onClose}>{de.app.close}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ProfilePage() {
  const { me } = useAuth()
  const sessions = useResource<Array<{
    id: string; createdAt: string; lastSeenAt: string
    userAgent: string | null; ipAddress: string | null; isCurrent: boolean
  }>>('/auth/sessions')
  const { toast, show, clear } = useToast()

  const revoke = async (sessionID: string) => {
    try {
      await api.delete(`/auth/sessions/${sessionID}`)
      sessions.reload()
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const revokeOthers = async () => {
    try {
      await api.delete('/auth/sessions')
      sessions.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  return (
    <>
      <PageHeader title={de.profile.title} subtitle={me?.email} />

      <div className="grid cols-2">
        <Card title={de.profile.title}>
          <dl className="kv">
            <dt>{de.accounts.columnName}</dt><dd>{me?.displayName}</dd>
            <dt>{de.accounts.columnEmail}</dt><dd>{me?.email}</dd>
            <dt>{de.accounts.columnRole}</dt><dd>{me?.role}</dd>
            <dt>{de.nav.organization}</dt><dd>{me?.organizationName ?? de.app.none}</dd>
            <dt>{de.organizations.timezone}</dt><dd className="mono">{me?.timezone ?? de.app.none}</dd>
          </dl>

          <a className="btn btn-ghost" href="/password" style={{ marginTop: 'var(--sp-3)' }}>
            {de.profile.changePassword}
          </a>
        </Card>

        <Card title={de.profile.sessions}
              actions={<button className="btn btn-ghost" onClick={() => void revokeOthers()}>
                {de.profile.endOthers}
              </button>}>
          <div className="help" style={{ marginBottom: 'var(--sp-3)' }}>{de.profile.sessionsHint}</div>

          <table><tbody>
            {(sessions.data ?? []).map(session => (
              <tr key={session.id}>
                <td className="sm">
                  {session.isCurrent && <strong>{de.profile.currentSession} · </strong>}
                  {session.userAgent?.slice(0, 40) ?? de.app.none}
                </td>
                <td className="mono faint xs">{session.ipAddress}</td>
                <td className="xs muted">{relative(session.lastSeenAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-ghost" onClick={() => void revoke(session.id)}>
                    {de.profile.endSession}
                  </button>
                </td>
              </tr>
            ))}
          </tbody></table>
        </Card>
      </div>

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}
